import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface Ticket {
  ticketId: string;
  title: string;
  description: string;
  /** API may send numeric enums (System.Text.Json without string enums). */
  status: 'open' | 'in_progress' | 'resolved' | 'closed' | number;
  priority: 'low' | 'medium' | 'high' | 'urgent' | number;
  type: 'bug' | 'feature_request' | 'support' | 'churn_signal' | 'general';
  assigneeId?: string;
  assigneeName?: string;
  assigneeEmail?: string;
  tags: string[];
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  churnSignalId?: string;
  churnSignalData?: {
    signalType: string;
    riskScore: number;
    userEmail: string;
    userName: string;
  };
  /** When present (e.g. tickets from churn signals), links back to `signalId` for filtering. */
  metadata?: Record<string, unknown>;

  /** Audience (customer) members this ticket applies to — from `ticket_audience_members`. */
  audienceLinks?: Array<{
    id?: string;
    audienceMemberId: string;
    audienceMemberEmail?: string | null;
    audienceMemberName?: string | null;
  }>;
  /** .NET API returns `id`; some UI code maps to `ticketId`. */
  id?: string;
  columnId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
  updatedByName?: string;
  commentCount?: number; // Number of comments for this ticket
  comments?: Comment[]; // Full comment objects (only populated in ticket details)
  archived?: boolean;
  upvotes?: string[];
  downvotes?: string[];
}

export interface Comment {
  commentId: string;
  ticketId: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface Column {
  id: string;
  name: string;
  description: string;
  color: string;
  position: number;
  isDefault: boolean;
  deleted: boolean;
}

export interface GetTicketsResponse {
  tickets: Ticket[];
  columns: Column[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export type FetchTicketsOptions = {
  /** Default 50 on API; use a higher value when filtering client-side (e.g. member profile). */
  limit?: number;
  /** When true, API includes archived tickets in the list (default: active only). */
  includeArchived?: boolean;
};

export async function fetchWorkspaceTickets(
  sessionJwt: string | undefined,
  workspaceId: string,
  options?: FetchTicketsOptions,
): Promise<GetTicketsResponse> {
  const bearer = resolveBearerToken(sessionJwt);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const params: Record<string, string | number | boolean> = {};
  if (options?.limit != null) params.limit = options.limit;
  if (options?.includeArchived) params.includeArchived = true;

  const response = await axios.get<GetTicketsResponse>(`${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets`, {
    headers,
    params: Object.keys(params).length > 0 ? params : undefined,
  });

  return response.data;
}

export function useGetTickets(options?: { includeArchived?: boolean; limit?: number }) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);

  // Helper function to extract workspace ID from workspace object
  const extractWorkspaceId = (workspaceObj: any) => {
    if (!workspaceObj) return '';
    // Try id first, then SK, then extract from SK if it starts with "WS#"
    if (workspaceObj.id) return workspaceObj.id;
    if (workspaceObj.SK) {
      // If SK starts with "WS#", extract the UUID part
      if (workspaceObj.SK.startsWith('WS#')) {
        return workspaceObj.SK.substring(3); // Remove "WS#" prefix
      }
      return workspaceObj.SK;
    }
    return '';
  };

  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const hasBearer = !!resolveBearerToken(sessionJwt);

  const includeArchived = options?.includeArchived ?? false;

  return useQuery<{ tickets: Ticket[]; columns: Column[] }>({
    queryKey: [queryKeys.tickets, workspaceId, includeArchived],
    queryFn: async () => {
      return fetchWorkspaceTickets(sessionJwt, workspaceId, {
        includeArchived: includeArchived || undefined,
        limit: options?.limit ?? 200,
      });
    },
    enabled: !!workspaceId && hasBearer,
    staleTime: 1000, // Short stale time (1 second) to allow optimistic updates to settle before refetch
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus for real-time updates
    // Default refetchOnMount (stale): opening the board after creating a ticket elsewhere must refetch
  });
}
